import { useState, useEffect } from 'react';
import { Handshake, Loader2, ExternalLink } from 'lucide-react';
import { getSponsors } from '../lib/api';

const SponsorCard = ({ sponsor }) => {
  const hasLogo = sponsor.logo_data && sponsor.logo_content_type;

  const content = (
    <div
      className="flex flex-col items-center gap-4 p-6 rounded-2xl border border-slate-700/50 bg-slate-900/50 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:border-primary/30"
      data-testid={`sponsor-card-${sponsor.id}`}
    >
      {hasLogo ? (
        <div className="w-36 h-36 sm:w-44 sm:h-44 rounded-xl overflow-hidden bg-white/10 flex items-center justify-center p-3">
          <img
            src={`data:${sponsor.logo_content_type};base64,${sponsor.logo_data}`}
            alt={sponsor.name}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      ) : (
        <div className="w-36 h-36 sm:w-44 sm:h-44 rounded-xl bg-slate-800/50 flex items-center justify-center">
          <Handshake className="w-12 h-12 text-slate-600" />
        </div>
      )}
      <div className="text-center">
        <h3 className="font-heading font-bold text-base sm:text-lg">{sponsor.name}</h3>
      </div>
      {sponsor.website_url && (
        <span className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-primary transition-colors">
          Visita sito <ExternalLink className="w-3 h-3" />
        </span>
      )}
    </div>
  );

  if (sponsor.website_url) {
    return (
      <a href={sponsor.website_url} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    );
  }
  return content;
};

export const SponsorPage = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getSponsors();
        // Sort alphabetically by name
        const sorted = [...res.data].sort((a, b) => a.name.localeCompare(b.name, 'it'));
        setSponsors(sorted);
      } catch (error) {
        console.error('Error fetching sponsors:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="py-8 sm:py-12 px-4 min-h-screen" data-testid="sponsor-page">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-4">
            <Handshake className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-primary uppercase tracking-wider">
              I Nostri Partner
            </span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black">
            <span className="text-primary">SPONSOR</span> DEL TORNEO
          </h1>
          <p className="mt-3 text-slate-400 text-sm sm:text-base max-w-xl mx-auto">
            Grazie ai nostri sponsor che rendono possibile il Torneo Notturno di Viadana
          </p>
        </div>

        {sponsors.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Handshake className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold mb-2">Sponsor in arrivo</h3>
            <p className="text-slate-400">
              Gli sponsor del torneo verranno pubblicati a breve
            </p>
          </div>
        ) : (
          <div className="flex flex-wrap justify-center gap-6">
            {sponsors.map(s => (
              <SponsorCard key={s.id} sponsor={s} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SponsorPage;
